import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import api from "../api/client";
import Navbar from "../components/layout/Navbar";
import MobileNavbar from "../components/layout/MobileNavbar";
import NapImportModal from "../components/modals/NapImportModal";
import NapEditModal from "../components/modals/NapEditModal";
import NapQrModal from "../components/modals/NapQrModal";
import NapMergeQrModal from "../components/modals/NapMergeQrModal";
import NapHistoryModal from "../components/modals/NapHistoryModal";
import NapDetailsResultModal from "../components/modals/NapDetailsResultModal";
import NapLocatorMap from "../components/map/NapLocatorMap";
import AddressSearchBar from "../components/map/AddressSearchBar";
import useAuth from "../hooks/useAuth";
import useIsMobile from "../hooks/useIsMobile";
import { useConfirm } from "../context/ConfirmContext";
import { MODULE_THEMES } from "../utils/moduleTheme";

export default function NapLocatorPage() {
  const { hasPerm } = useAuth();
  const isMobile = useIsMobile();
  const { confirm } = useConfirm();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(() => searchParams.get("search") || "");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showImport, setShowImport] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [qrModal, setQrModal] = useState(null); // { record, mode, previewData }
  const [mobileQrView, setMobileQrView] = useState(null); // { qrData, napCode } for fullscreen scanning
  const [generatingQrId, setGeneratingQrId] = useState(null);
  const [fetchingNapDetailsId, setFetchingNapDetailsId] = useState(null);
  const [napDetailsModal, setNapDetailsModal] = useState(null); // data from S2S API
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [mergeModal, setMergeModal] = useState(null); // { previewData }
  const [historyModal, setHistoryModal] = useState(null); // { record }
  const [sortBy, setSortBy] = useState("nap_code");
  const [sortDir, setSortDir] = useState("asc");
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem("napLocatorViewMode");
    return saved === "map" ? "map" : "table";
  });
  const [mapPoints, setMapPoints] = useState([]);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState(false);
  const [detailPoint, setDetailPoint] = useState(null); // NAP point clicked on map
  const [flyToPoint, setFlyToPoint] = useState(null); // point to fly to on map
  const [napHighlight, setNapHighlight] = useState(null); // [lat,lng] pulsating highlight from a detail-modal link
  const [searchPin, setSearchPin] = useState(null); // { lat, lng, address } from address search
  const [stats, setStats] = useState({ available_ports: 0, full_nap: 0, no_stencil: 0, not_yet_updated: 0, total: 0 });
  const [filterStat, setFilterStat] = useState(""); // "", "available_ports", "full_nap", "no_stencil", "not_yet_updated"
  const pageSize = 100;
  const NAP = MODULE_THEMES.napLocator;

  const canEdit = hasPerm("nap_locator.edit");
  const canDelete = hasPerm("nap_locator.delete");
  const canQrGenerate = hasPerm("nap_locator.qr_generate");
  const canQrDelete = hasPerm("nap_locator.qr_delete");
  const canMergeDetails = hasPerm("nap_locator.merge_details");
  const canBatchDetails = hasPerm("nap_locator.batch_details");
  const hasActions = canEdit || canDelete;

  // Focus a specific NAP when navigated here from a Job Order / Migration detail modal:
  // switch to map view and fly to it (zoom 18), showing its detail alongside the markers.
  const focusNapHandled = useRef(false);
  useEffect(() => {
    if (focusNapHandled.current) return;
    const f = location.state?.focusNap;
    if (!f || f.latitude == null || f.longitude == null) return;
    focusNapHandled.current = true;
    const lat = Number(f.latitude), lng = Number(f.longitude);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    setViewMode("map");
    setFlyToPoint({ latitude: lat, longitude: lng });
    setDetailPoint({ id: f.id ?? null, nap_code: f.nap_code, latitude: lat, longitude: lng });
    setNapHighlight([lat, lng]);
  }, [location.state]);

  // Derive LCP code from a NAP code (first two parts)
  const getLcp = (napCode) => {
    if (!napCode) return "";
    const parts = napCode.trim().split(/\s+/);
    return parts.length >= 2 ? `${parts[0]} ${parts[1]}` : napCode;
  };

  // Determine the LCP of currently selected records (for constraining further selections)
  const selectedLcp = useMemo(() => {
    if (selectedIds.size === 0) return null;
    const firstSelectedRecord = records.find((r) => selectedIds.has(r.id));
    return firstSelectedRecord ? getLcp(firstSelectedRecord.nap_code) : null;
  }, [selectedIds, records]);

  // Group individual (non-merged) NAPs by lat/lon for visual grouping
  const locationGroupColors = useMemo(() => {
    const colors = [
      "border-l-blue-400 bg-blue-50/40",
      "border-l-green-400 bg-green-50/40",
      "border-l-purple-400 bg-purple-50/40",
      "border-l-orange-400 bg-orange-50/40",
      "border-l-pink-400 bg-pink-50/40",
      "border-l-cyan-400 bg-cyan-50/40",
    ];
    const isIndividual = (napCode) => {
      if (!napCode) return false;
      const match = napCode.match(/NP(\d+)$/i);
      return !!match;
    };
    const groups = {};
    for (const r of records) {
      if (!isIndividual(r.nap_code) || r.latitude == null || r.longitude == null) continue;
      const key = `${Number(r.latitude).toFixed(5)}_${Number(r.longitude).toFixed(5)}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(r.id);
    }
    const colorMap = {};
    let colorIdx = 0;
    for (const [, ids] of Object.entries(groups)) {
      if (ids.length < 2) continue;
      const cls = colors[colorIdx % colors.length];
      for (const id of ids) colorMap[id] = cls;
      colorIdx++;
    }
    return colorMap;
  }, [records]);

  // Persist view mode across refreshes
  useEffect(() => {
    localStorage.setItem("napLocatorViewMode", viewMode);
  }, [viewMode]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset page when filter changes
  const handleFilterStat = (key) => {
    setFilterStat((prev) => (prev === key ? "" : key));
    setPage(1);
  };

  // On mobile, require at least 2 parts with 3+ chars in 2nd part (LCP code e.g. "PSG003 LP5")
  const mobileSearchValid = useMemo(() => {
    if (!isMobile) return true;
    const parts = debouncedSearch.trim().split(/\s+/);
    return parts.length >= 2 && parts[0].length > 0 && parts[1].length >= 3;
  }, [isMobile, debouncedSearch]);

  const fetchRecords = useCallback(async () => {
    // On mobile, skip fetch if search is not valid (require LCP code)
    if (!mobileSearchValid) {
      setRecords([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = { search: debouncedSearch, page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir, stat_filter: filterStat };
      const [listRes, countRes, statsRes] = await Promise.all([
        api.get("/nap-locator", { params }),
        api.get("/nap-locator/count", { params: { search: debouncedSearch, stat_filter: filterStat } }),
        api.get("/nap-locator/stats", { params: { search: debouncedSearch } }),
      ]);
      setRecords(listRes.data);
      setTotalCount(countRes.data.total);
      setStats(statsRes.data);
    } catch (err) {
      console.error("Failed to fetch NAP records", err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, sortBy, sortDir, filterStat, mobileSearchValid]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // Fetch map points when in map view or when search changes
  const fetchMapPoints = useCallback(async (silent = false) => {
    if (!silent) setMapLoading(true);
    setMapError(false);
    try {
      const { data } = await api.get("/nap-locator/map-points", {
        params: { search: debouncedSearch },
      });
      setMapPoints(data);
    } catch (err) {
      console.error("Failed to fetch map points", err);
      if (!silent) setMapError(true);
    } finally {
      if (!silent) setMapLoading(false);
    }
  }, [debouncedSearch]);

  const mapInitialized = useRef(false);
  useEffect(() => {
    if (viewMode === "map") {
      // Use silent mode for subsequent fetches so the map doesn't unmount/reset
      const silent = mapInitialized.current && mapPoints.length > 0;
      fetchMapPoints(silent);
      mapInitialized.current = true;
    } else {
      mapInitialized.current = false;
    }
  }, [viewMode, fetchMapPoints]);

  // Filter map points client-side based on stat filter
  const filteredMapPoints = useMemo(() => {
    if (!filterStat) return mapPoints;
    return mapPoints.filter((pt) => {
      if (filterStat === "available_ports") return pt.vacant != null && pt.vacant > 0;
      if (filterStat === "full_nap") return pt.vacant != null && pt.vacant === 0;
      if (filterStat === "no_stencil") return pt.stencil === "N";
      if (filterStat === "not_yet_updated") return pt.stencil == null;
      return true;
    });
  }, [mapPoints, filterStat]);

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get("/nap-locator/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "nap_locator_template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to download template.");
    }
  };

  const handleDelete = async (record) => {
    const ok = await confirm({
      title: "Delete NAP Record",
      message: `Are you sure you want to delete "${record.nap_code}"? This action cannot be undone.`,
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/nap-locator/${record.id}`);
      fetchRecords();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete record.");
    }
  };

  const handleGenerateQr = async (record) => {
    setGeneratingQrId(record.id);
    try {
      const { data } = await api.post(`/nap-locator/${record.id}/qr/preview`);
      setQrModal({ record, mode: "preview", previewData: data });
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to generate QR preview.");
    } finally {
      setGeneratingQrId(null);
    }
  };

  const handleNapDetails = async (record) => {
    setFetchingNapDetailsId(record.id);
    try {
      const { data } = await api.post(`/nap-locator/${record.id}/nap-details`);
      if (data.found) {
        // Show the details modal (replicating mobile app appearance).
        // Stash the record id so the modal's Refresh button can force a re-fetch.
        setNapDetailsModal({ ...data, _recordId: record.id });
        fetchRecords();
      } else {
        // No data found — show system-standard confirm dialog, then mark as "N"
        await confirm({
          title: "No NAP Details Found",
          message: data.detail || `No NAP details found for "${record.nap_code}" on the external server.`,
          confirmLabel: "OK",
          cancelLabel: "Close",
        });
        try {
          await api.post(`/nap-locator/${record.id}/no-stencil`);
          fetchRecords();
        } catch (e) {
          // silent fail on marking no-stencil
        }
      }
    } catch (err) {
      await confirm({
        title: "NAP Details Error",
        message: err.response?.data?.detail || "Failed to fetch NAP details from external server.",
        confirmLabel: "OK",
        cancelLabel: "Close",
        variant: "danger",
      });
    } finally {
      setFetchingNapDetailsId(null);
    }
  };

  const handleViewQr = (record) => {
    setQrModal({ record, mode: "view", previewData: null });
  };

  const handleMergeQrPreview = async () => {
    try {
      const ids = Array.from(selectedIds);
      const { data } = await api.post("/nap-locator/merge-qr/preview", { record_ids: ids });
      setMergeModal({ previewData: data });
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to generate merge QR preview.");
    }
  };

  const [fetchingMergeNapDetails, setFetchingMergeNapDetails] = useState(false);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null); // { processed, total, current }
  const [batchForceResync, setBatchForceResync] = useState(false);
  const [batchTaskId, setBatchTaskId] = useState(null);
  const [batchCancelConfirm, setBatchCancelConfirm] = useState(false);

  const handleMergeNapDetails = async () => {
    setFetchingMergeNapDetails(true);
    try {
      const ids = Array.from(selectedIds);
      const { data } = await api.post("/nap-locator/merge-nap-details", { record_ids: ids });
      if (data.found) {
        // Show the NAP details modal with a save callback for merge
        setNapDetailsModal({ ...data, _isMerge: true, _recordIds: ids });
      } else {
        await confirm({
          title: "No NAP Details Found",
          message: data.detail || `No NAP details found for "${data.merged_nap_code}" on the external server.`,
          confirmLabel: "OK",
          cancelLabel: "Close",
        });
      }
    } catch (err) {
      await confirm({
        title: "NAP Details Error",
        message: err.response?.data?.detail || "Failed to fetch NAP details from external server.",
        confirmLabel: "OK",
        cancelLabel: "Close",
        variant: "danger",
      });
    } finally {
      setFetchingMergeNapDetails(false);
    }
  };

  const handleBatchNapDetails = async () => {
    if (!debouncedSearch.trim()) return;
    const ok = await confirm({
      title: "Batch NAP Details",
      message: `This will fetch NAP details for ALL records matching "${debouncedSearch}". This may take a while. Continue?`,
      confirmLabel: "Proceed",
      cancelLabel: "Cancel",
    });
    if (!ok) return;

    setBatchProcessing(true);
    setBatchProgress({ processed: 0, total_groups: 0, success: 0, no_data: 0, errors: 0, skipped: 0, current: "Starting..." });
    setBatchCancelConfirm(false);
    try {
      const { data } = await api.post("/nap-locator/batch-nap-details", { search: debouncedSearch.trim(), force_resync: batchForceResync });
      const taskId = data.task_id;
      setBatchTaskId(taskId);

      // Poll for progress
      const poll = async () => {
        while (true) {
          await new Promise((r) => setTimeout(r, 2000));
          try {
            const { data: status } = await api.get(`/nap-locator/batch-nap-details/${taskId}`);
            setBatchProgress(status);
            if (status.status === "completed") {
              setBatchProcessing(false);
              setBatchProgress(null);
              setBatchTaskId(null);
              await confirm({
                title: "Batch NAP Details Complete",
                message: `Processed: ${status.processed} groups\nSuccess: ${status.success}\nNo Data: ${status.no_data}\nErrors: ${status.errors}\nSkipped (already saved): ${status.skipped ?? 0}`,
                confirmLabel: "OK",
                cancelLabel: "Close",
              });
              fetchRecords();
              return;
            } else if (status.status === "cancelled") {
              setBatchProcessing(false);
              setBatchProgress(null);
              setBatchTaskId(null);
              await confirm({
                title: "Batch NAP Details Cancelled",
                message: `Cancelled.\nProcessed: ${status.processed} groups\nSuccess: ${status.success}\nNo Data: ${status.no_data}\nErrors: ${status.errors}\nSkipped (already saved): ${status.skipped ?? 0}`,
                confirmLabel: "OK",
                cancelLabel: "Close",
              });
              fetchRecords();
              return;
            } else if (status.status === "failed") {
              setBatchProcessing(false);
              setBatchProgress(null);
              setBatchTaskId(null);
              await confirm({
                title: "Batch NAP Details Error",
                message: status.error || "Batch processing failed unexpectedly.",
                confirmLabel: "OK",
                cancelLabel: "Close",
                variant: "danger",
              });
              fetchRecords();
              return;
            }
          } catch (pollErr) {
            // Continue polling on transient errors
            console.error("Poll error:", pollErr);
          }
        }
      };
      await poll();
    } catch (err) {
      setBatchProcessing(false);
      setBatchProgress(null);
      setBatchTaskId(null);
      await confirm({
        title: "Batch NAP Details Error",
        message: err.response?.data?.detail || "Failed to start batch NAP details.",
        confirmLabel: "OK",
        cancelLabel: "Close",
        variant: "danger",
      });
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
    setPage(1);
  };

  const SortHeader = ({ column, children, className = "" }) => (
    <th
      className={`px-3 py-2 font-semibold cursor-pointer select-none hover:bg-gray-200 ${className}`}
      onClick={() => handleSort(column)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortBy === column && (
          <span className="text-blue-600">{sortDir === "asc" ? "▲" : "▼"}</span>
        )}
      </span>
    </th>
  );

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="h-screen flex flex-col bg-gray-50 relative">
      {/* Full-page loading overlay when fetching NAP details */}
      {(fetchingNapDetailsId || fetchingMergeNapDetails) && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20">
          <div className="relative flex items-center justify-center w-20 h-20">
            {/* Rotating hexagon border */}
            <svg
              className="absolute inset-0 w-full h-full animate-spin"
              style={{ animationDuration: "1.5s" }}
              viewBox="0 0 100 100"
              fill="none"
            >
              <polygon
                points="50,2 93,25 93,75 50,98 7,75 7,25"
                stroke="#2563eb"
                strokeWidth="4"
                fill="none"
                strokeLinecap="round"
                strokeDasharray="12 6"
              />
            </svg>
            {/* Static icon */}
            <img
              src="/best-icon.png"
              alt="Loading..."
              className="h-12 w-12"
            />
          </div>
        </div>
      )}
      {/* Batch processing overlay */}
      {batchProcessing && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4 text-center">
            <div className="relative flex items-center justify-center w-16 h-16 mx-auto mb-4">
              <svg
                className="absolute inset-0 w-full h-full animate-spin"
                style={{ animationDuration: "1.5s" }}
                viewBox="0 0 100 100"
                fill="none"
              >
                <polygon
                  points="50,2 93,25 93,75 50,98 7,75 7,25"
                  stroke="#ea580c"
                  strokeWidth="4"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray="12 6"
                />
              </svg>
              <img src="/best-icon.png" alt="Processing..." className="h-10 w-10" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-1">Batch NAP Details</h3>
            {batchProgress && batchProgress.total_groups > 0 ? (
              <>
                <p className="text-sm text-gray-600">
                  Processing group {batchProgress.processed} of {batchProgress.total_groups}
                </p>
                <p className="text-xs text-gray-500 mt-1 truncate max-w-[280px] mx-auto">
                  {batchProgress.current && `Current: ${batchProgress.current}`}
                </p>
                <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${Math.round((batchProgress.processed / batchProgress.total_groups) * 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  ✓ {batchProgress.success} success · — {batchProgress.no_data} no data · ✗ {batchProgress.errors} errors · ⊘ {batchProgress.skipped ?? 0} skipped
                </p>
                <div className="mt-4">
                  {!batchCancelConfirm ? (
                    <button
                      onClick={() => setBatchCancelConfirm(true)}
                      className="px-4 py-1.5 text-sm bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                    >
                      Cancel
                    </button>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-sm text-red-600 font-medium">Stop processing?</span>
                      <button
                        onClick={async () => {
                          try {
                            await api.post(`/nap-locator/batch-nap-details/${batchTaskId}/cancel`);
                          } catch (e) { /* ignore */ }
                          setBatchCancelConfirm(false);
                        }}
                        className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                      >
                        Yes, Stop
                      </button>
                      <button
                        onClick={() => setBatchCancelConfirm(false)}
                        className="px-3 py-1 text-sm bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
                      >
                        No
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-600">Starting batch processing...</p>
            )}
          </div>
        </div>
      )}
      {isMobile ? <MobileNavbar /> : <Navbar />}
      <div className={`flex-1 flex flex-col min-h-0 ${isMobile ? "px-3 pt-2 pb-2" : "px-4 lg:px-6 pt-3 pb-2"} w-full relative`}>
        {/* Top control row: Search + buttons + toggle */}
        <div className="flex-shrink-0 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
          {/* Left side: Search */}
          <div className="flex items-center gap-2 flex-1">
            <input
              type="text"
              placeholder={isMobile ? "Search NAP Code (e.g. PSG003 LP5)..." : "Search NAP Code or Area..."}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`${isMobile && viewMode === "map" ? "w-[35%]" : "w-full sm:w-80"} px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            {viewMode === "map" && (
              <div className={isMobile ? "flex-1" : ""}>
                <AddressSearchBar
                  placeholder={isMobile ? "Search addr..." : "Search address or place..."}
                  onPlaceSelected={(place) => {
                    setFlyToPoint({ latitude: place.lat, longitude: place.lng });
                    setSearchPin({ lat: place.lat, lng: place.lng, address: place.address });
                  }}
                  onClear={() => setSearchPin(null)}
                />
              </div>
            )}
            {search && (
              <button
                onClick={() => setSearch("")}
                className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-100 font-medium whitespace-nowrap"
              >
                Clear
              </button>
            )}
            {!isMobile && debouncedSearch.trim() && canBatchDetails && (
              <button
                onClick={handleBatchNapDetails}
                disabled={batchProcessing}
                className="px-4 py-2 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 font-medium whitespace-nowrap disabled:opacity-50"
              >
                {batchProcessing ? "Processing..." : "Batch NAP Details"}
              </button>
            )}
            {!isMobile && debouncedSearch.trim() && canBatchDetails && (
              <label
                className="flex items-center gap-1.5 text-sm text-gray-700 whitespace-nowrap cursor-pointer select-none"
                title="By default, records already saved locally are skipped. Check this to re-fetch and overwrite them from the external server."
              >
                <input
                  type="checkbox"
                  checked={batchForceResync}
                  onChange={(e) => setBatchForceResync(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                Re-fetch already-saved
              </label>
            )}
            {!isMobile && viewMode === "table" && selectedIds.size >= 2 && canMergeDetails && (
              <button
                onClick={handleMergeNapDetails}
                disabled={fetchingMergeNapDetails}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 font-medium whitespace-nowrap disabled:opacity-50"
              >
                {fetchingMergeNapDetails ? "Fetching..." : `NAP Details (${selectedIds.size} selected)`}
              </button>
            )}
            {!isMobile && viewMode === "table" && selectedIds.size >= 2 && canQrGenerate && (
              <button
                onClick={handleMergeQrPreview}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 font-medium whitespace-nowrap"
              >
                Generate QR ({selectedIds.size} selected)
              </button>
            )}
            {!isMobile && viewMode === "table" && selectedIds.size > 0 && (
              <button
                onClick={() => setSelectedIds(new Set())}
                className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-100 whitespace-nowrap"
              >
                Clear
              </button>
            )}
          </div>
          {/* Right side: Buttons + Toggle */}
          <div className="flex items-center gap-2">
            {!isMobile && hasPerm("nap_locator.export") && (
              <button
                onClick={handleDownloadTemplate}
                className="px-4 py-1.5 text-sm border border-blue-600 text-blue-600 rounded hover:bg-blue-50 font-medium"
              >
                Download Template
              </button>
            )}
            {!isMobile && hasPerm("nap_locator.import") && (
              <button
                onClick={() => setShowImport(true)}
                className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
              >
                Import
              </button>
            )}
            <div
              className="flex rounded-lg border border-gray-300 overflow-hidden cursor-pointer"
              onClick={() => { setSelectedIds(new Set()); setViewMode(viewMode === "table" ? "map" : "table"); }}
            >
              <span className={`px-3 py-1.5 text-sm font-medium flex items-center gap-1 ${viewMode === "table" ? "bg-blue-600 text-white" : "bg-white text-gray-600"}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 6h18M3 14h18M3 18h18" /></svg>
                Table
              </span>
              <span className={`px-3 py-1.5 text-sm font-medium flex items-center gap-1 ${viewMode === "map" ? "bg-blue-600 text-white" : "bg-white text-gray-600"}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>
                Map
              </span>
            </div>
          </div>
        </div>

        {/* Statistics row: Module badge + Stats cards (hidden on mobile) */}
        {!isMobile && (
        <div className="flex-shrink-0 flex flex-wrap items-stretch gap-2 mb-3">
          {/* Module badge */}
          <div className={`${NAP.badge} rounded-lg shadow-sm hidden sm:flex items-center gap-2 px-4 py-2`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d={NAP.iconPath} /></svg>
            <span className="text-xs font-bold uppercase tracking-wider">NAP Locator</span>
          </div>
          {/* Stats cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 flex-1">
            {/* Available NAP */}
            <div
              onClick={() => stats.available_ports > 0 && handleFilterStat("available_ports")}
              className={`rounded-lg px-3 py-2 text-center border bg-green-100 text-green-800 border-green-200 transition-all hover:shadow cursor-pointer ${
                filterStat === "available_ports" ? "ring-2 ring-blue-600 border-blue-600 shadow-md" : ""
              }`}
              style={{ opacity: stats.available_ports === 0 && !loading ? 0.5 : 1 }}
            >
              <div className="flex items-baseline justify-center gap-1.5 leading-tight">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Available NAP</span>
              </div>
              <span className="text-lg font-bold text-gray-900 tabular-nums">{loading ? "..." : stats.available_ports.toLocaleString()}</span>
            </div>
            {/* Full NAP */}
            <div
              onClick={() => stats.full_nap > 0 && handleFilterStat("full_nap")}
              className={`rounded-lg px-3 py-2 text-center border bg-red-100 text-red-800 border-red-200 transition-all hover:shadow cursor-pointer ${
                filterStat === "full_nap" ? "ring-2 ring-blue-600 border-blue-600 shadow-md" : ""
              }`}
              style={{ opacity: stats.full_nap === 0 && !loading ? 0.5 : 1 }}
            >
              <div className="flex items-baseline justify-center gap-1.5 leading-tight">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Full NAP</span>
              </div>
              <span className="text-lg font-bold text-gray-900 tabular-nums">{loading ? "..." : stats.full_nap.toLocaleString()}</span>
            </div>
            {/* No Stencil */}
            <div
              onClick={() => stats.no_stencil > 0 && handleFilterStat("no_stencil")}
              className={`rounded-lg px-3 py-2 text-center border bg-blue-100 text-blue-800 border-blue-200 transition-all hover:shadow cursor-pointer ${
                filterStat === "no_stencil" ? "ring-2 ring-blue-600 border-blue-600 shadow-md" : ""
              }`}
              style={{ opacity: stats.no_stencil === 0 && !loading ? 0.5 : 1 }}
            >
              <div className="flex items-baseline justify-center gap-1.5 leading-tight">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">No Record</span>
              </div>
              <span className="text-lg font-bold text-gray-900 tabular-nums">{loading ? "..." : stats.no_stencil.toLocaleString()}</span>
            </div>
            {/* Not Yet Updated */}
            <div
              onClick={() => stats.not_yet_updated > 0 && handleFilterStat("not_yet_updated")}
              className={`rounded-lg px-3 py-2 text-center border bg-yellow-100 text-yellow-800 border-yellow-200 transition-all hover:shadow cursor-pointer ${
                filterStat === "not_yet_updated" ? "ring-2 ring-blue-600 border-blue-600 shadow-md" : ""
              }`}
              style={{ opacity: stats.not_yet_updated === 0 && !loading ? 0.5 : 1 }}
            >
              <div className="flex items-baseline justify-center gap-1.5 leading-tight">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Not Yet Updated</span>
              </div>
              <span className="text-lg font-bold text-gray-900 tabular-nums">{loading ? "..." : stats.not_yet_updated.toLocaleString()}</span>
            </div>
          </div>
        </div>
        )}

        {/* TABLE VIEW */}
        {viewMode === "table" && (
          <>
            {/* Mobile card layout */}
            {isMobile ? (
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-20 text-gray-400">Loading…</div>
                ) : !mobileSearchValid ? (
                  <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                    <p className="text-sm">Search by LCP code to view records</p>
                    <p className="text-xs mt-1 text-gray-300">e.g. "PSG003 LP5"</p>
                  </div>
                ) : records.length === 0 ? (
                  <div className="flex items-center justify-center py-20 text-gray-400">No NAP records found.</div>
                ) : (
                  <div className="space-y-2 pb-4">
                    {records.map((r, idx) => (
                      <div key={r.id} className={`rounded-xl border shadow-sm p-3.5 ${locationGroupColors[r.id] ? `border-l-4 ${locationGroupColors[r.id]}` : "bg-white"}`}>
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-[11px] font-bold flex items-center justify-center">
                              {(page - 1) * pageSize + idx + 1}
                            </span>
                            <span className="text-sm font-semibold text-gray-900 truncate">{r.nap_code}</span>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {r.qr_code_data && (
                              <button
                                onClick={() => setMobileQrView({ qrData: r.qr_code_data, napCode: r.nap_code })}
                                className="w-8 h-8 flex items-center justify-center rounded bg-gray-100 active:bg-gray-200"
                                title="View QR Code"
                              >
                                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h7v7H3V3zm11 0h7v7h-7V3zm-11 11h7v7H3v-7zm14 3h.01M17 17h.01M21 17h.01M14 14h3v3h-3v-3zm3 3h3v3h-3v-3z" />
                                </svg>
                              </button>
                            )}
                            {r.latitude && r.longitude && (
                              <button
                                onClick={() => {
                                  setFlyToPoint({ latitude: r.latitude, longitude: r.longitude, id: r.id });
                                  setDetailPoint({ id: r.id, nap_code: r.nap_code, latitude: r.latitude, longitude: r.longitude });
                                  setSelectedIds(new Set());
                                  setViewMode("map");
                                }}
                                className="flex-shrink-0 px-2 py-1 text-[10px] bg-blue-100 text-blue-700 rounded font-medium"
                              >
                                View on Map
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-x-3 gap-y-1 text-xs text-gray-600 ml-8">
                          <div><span className="text-gray-400">Ports:</span> {r.no_of_ports ?? "—"}</div>
                          <div><span className="text-gray-400">Vacant:</span> {r.vacant ?? "—"}</div>
                          <div><span className="text-gray-400">Record:</span> {r.stencil ?? "—"}</div>
                          {r.area && <div className="col-span-3"><span className="text-gray-400">Area:</span> {r.area}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {/* Mobile pagination */}
                {mobileSearchValid && totalCount > 0 && (
                  <div className="flex-shrink-0 flex items-center justify-between py-3 text-xs text-gray-500">
                    <span>Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount}</span>
                    {totalPages > 1 && (
                      <div className="flex items-center gap-2">
                        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="px-2 py-1 border rounded disabled:opacity-40">Prev</button>
                        <span>{page}/{totalPages}</span>
                        <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-2 py-1 border rounded disabled:opacity-40">Next</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
            <>
            {/* Desktop table */}
            <div className="flex-1 flex flex-col min-h-0 bg-white rounded shadow overflow-hidden">
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 sticky top-0 z-10">
                <tr className="text-gray-600 text-left">
                  <th className="px-3 py-2 font-semibold">#</th>
                  {canQrGenerate && <th className="px-2 py-2"></th>}
                  <SortHeader column="nap_code">NAP Code</SortHeader>
                  <th className="px-2 py-2"></th>
                  <SortHeader column="no_of_ports">No of Ports</SortHeader>
                  <SortHeader column="latitude">Latitude</SortHeader>
                  <SortHeader column="longitude">Longitude</SortHeader>
                  <SortHeader column="area">Area</SortHeader>
                  <SortHeader column="used_port">Used Port</SortHeader>
                  <SortHeader column="vacant">Unused/Vacant Port</SortHeader>
                  <SortHeader column="onu_connect">ONU Connect</SortHeader>
                  <SortHeader column="stencil">With Record</SortHeader>
                  <th className="px-3 py-2 font-semibold text-center">QR Code</th>
                  {hasActions && <th className="px-3 py-2 font-semibold">Actions</th>}
                  <SortHeader column="updated_at">Updated By</SortHeader>
                </tr>
              </thead>
              <tbody>
              {loading ? (
                <tr>
                  <td colSpan={13 + (canQrGenerate ? 1 : 0) + (hasActions ? 1 : 0)} className="text-center py-8 text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={13 + (canQrGenerate ? 1 : 0) + (hasActions ? 1 : 0)} className="text-center py-8 text-gray-400">
                    No NAP records found.
                  </td>
                </tr>
              ) : (
                records.map((r, idx) => (
                  <tr
                    key={r.id}
                    className={`${locationGroupColors[r.id] ? `border-l-4 ${locationGroupColors[r.id]}` : (idx % 2 === 0 ? "bg-white" : "bg-gray-50")}`}
                  >
                    <td className="px-3 py-2 text-gray-500">{(page - 1) * pageSize + idx + 1}</td>
                    {canQrGenerate && (
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(r.id)}
                          disabled={r.stencil === "Y" || (selectedLcp !== null && !selectedIds.has(r.id) && getLcp(r.nap_code) !== selectedLcp)}
                          onChange={(e) => {
                            const next = new Set(selectedIds);
                            if (e.target.checked) next.add(r.id);
                            else next.delete(r.id);
                            setSelectedIds(next);
                          }}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-40 disabled:cursor-not-allowed"
                        />
                      </td>
                    )}
                    <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">{r.nap_code}</td>
                    <td className="px-2 py-2">
                      {r.latitude && r.longitude && (
                        <button
                          onClick={() => {
                            setFlyToPoint({ latitude: r.latitude, longitude: r.longitude, id: r.id });
                            setDetailPoint({ id: r.id, nap_code: r.nap_code, latitude: r.latitude, longitude: r.longitude });
                            setSelectedIds(new Set());
                            setViewMode("map");
                          }}
                          className="px-1.5 py-0.5 text-[10px] bg-blue-100 text-blue-700 rounded hover:bg-blue-200 font-medium whitespace-nowrap"
                        >
                          View on Map
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2">{r.no_of_ports ?? "—"}</td>
                    <td className="px-3 py-2">{r.latitude ?? "—"}</td>
                    <td className="px-3 py-2">{r.longitude ?? "—"}</td>
                    <td className="px-3 py-2">{r.area ?? "—"}</td>
                    <td className="px-3 py-2">{r.used_port ?? "—"}</td>
                    <td className="px-3 py-2">{r.vacant ?? "—"}</td>
                    <td className="px-3 py-2">{r.onu_connect ?? "—"}</td>
                    <td className="px-3 py-2">{r.stencil ?? "—"}</td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {canQrGenerate && (
                          <button
                            onClick={() => handleNapDetails(r)}
                            disabled={fetchingNapDetailsId === r.id}
                            className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                          >
                            {fetchingNapDetailsId === r.id ? "Fetching..." : "NAP Details"}
                          </button>
                        )}
                        {r.qr_code_data ? (
                          <img
                            src={`data:image/png;base64,${r.qr_code_data}`}
                            alt="QR"
                            className="w-10 h-10 inline-block cursor-pointer rounded border hover:opacity-80"
                            onClick={() => handleViewQr(r)}
                          />
                        ) : canQrGenerate ? (
                          <button
                            onClick={() => handleGenerateQr(r)}
                            disabled={generatingQrId === r.id}
                            className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                            title="Manual QR Generation"
                          >
                            {generatingQrId === r.id ? "..." : "Generate QR"}
                          </button>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </div>
                    </td>
                    {hasActions && (
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          {canEdit && (
                            <button
                              onClick={() => setEditRecord(r)}
                              className="px-2 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600"
                            >
                              Edit
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => handleDelete(r)}
                              className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="px-3 py-2">
                      {r.updated_by_name ? (
                        <button
                          onClick={() => setHistoryModal({ record: r })}
                          className="text-blue-600 hover:underline text-xs"
                        >
                          {r.updated_by_name}
                        </button>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            </table>
          </div>
        </div>

        {/* Pagination - visible when records exist */}
        {mobileSearchValid && totalCount > 0 && (
        <div className="flex-shrink-0 flex items-center justify-between py-3 text-sm text-gray-600">
          <span>
            Showing {totalCount > 0 ? (page - 1) * pageSize + 1 : 0}–{Math.min(page * pageSize, totalCount)} of {totalCount}
          </span>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-gray-100"
              >
                Prev
              </button>
              <span>Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-gray-100"
              >
                Next
              </button>
            </div>
          )}
        </div>
        )}
          </>
          )}
          </>
        )}

        {/* MAP VIEW */}
        {viewMode === "map" && (
          <NapLocatorMap
            points={filteredMapPoints}
            loading={mapLoading}
            error={mapError}
            onPointClick={(pt) => setDetailPoint(pt)}
            onRetry={fetchMapPoints}
            flyTo={flyToPoint}
            onFlyDone={() => setFlyToPoint(null)}
            searchPin={searchPin}
            highlight={napHighlight}
            selectedPoint={detailPoint}
            onCloseDetail={() => setDetailPoint(null)}
            onRecordUpdated={() => {
              fetchRecords();
              fetchMapPoints(true);
            }}
            onViewInTable={(lcp) => {
              setDetailPoint(null);
              setSearch(lcp);
              setSelectedIds(new Set());
              setViewMode("table");
            }}
            mobileMode={isMobile}
            onShowQr={(qrData, napCode) => setMobileQrView({ qrData, napCode })}
          />
        )}
      </div>

      {/* Import Modal */}
      {showImport && (
        <NapImportModal
          onClose={() => setShowImport(false)}
          onRefresh={fetchRecords}
        />
      )}

      {/* Edit Modal */}
      {editRecord && (
        <NapEditModal
          record={editRecord}
          onClose={() => setEditRecord(null)}
          onSaved={fetchRecords}
        />
      )}

      {/* QR Code Modal */}
      {qrModal && (
        <NapQrModal
          record={qrModal.record}
          mode={qrModal.mode}
          previewData={qrModal.previewData}
          onClose={() => setQrModal(null)}
          onSaved={fetchRecords}
          canDeleteQr={canQrDelete}
          canEdit={canEdit}
        />
      )}

      {/* Merge QR Modal */}
      {mergeModal && (
        <NapMergeQrModal
          previewData={mergeModal.previewData}
          onClose={() => setMergeModal(null)}
          onSaved={() => { setSelectedIds(new Set()); fetchRecords(); }}
        />
      )}

      {/* History Modal */}
      {historyModal && (
        <NapHistoryModal
          record={historyModal.record}
          onClose={() => setHistoryModal(null)}
        />
      )}

      {/* NAP Details Result Modal */}
      {napDetailsModal && (
        <NapDetailsResultModal
          data={napDetailsModal}
          onChanged={fetchRecords}
          onClose={() => { setNapDetailsModal(null); setSelectedIds(new Set()); fetchRecords(); }}
          onMergeSave={napDetailsModal._isMerge ? async (modalData) => {
            await api.post("/nap-locator/merge-nap-details/save", {
              record_ids: modalData._recordIds,
              used_port: modalData.used_port,
              vacant: modalData.vacant,
              onu_connect: modalData.onu_connect,
              audit: modalData.audit || [],
              last_audit: modalData.last_audit ?? null,
              is_scan_completed: modalData.is_scan_completed ?? false,
            });
            setSelectedIds(new Set());
          } : undefined}
          onRefresh={!napDetailsModal._isMerge && napDetailsModal._recordId ? async () => {
            const { data } = await api.post(`/nap-locator/${napDetailsModal._recordId}/nap-details?force=true`);
            if (data.found) {
              setNapDetailsModal({ ...data, _recordId: napDetailsModal._recordId });
              fetchRecords();
            } else {
              // External server no longer has data for this NAP — close and mark as 'N'
              setNapDetailsModal(null);
              try {
                await api.post(`/nap-locator/${napDetailsModal._recordId}/no-stencil`);
              } catch (e) { /* silent */ }
              fetchRecords();
              await confirm({
                title: "No NAP Details Found",
                message: data.detail || "No NAP details found on the external server.",
                confirmLabel: "OK",
                cancelLabel: "Close",
              });
            }
          } : undefined}
        />
      )}

      {/* Mobile Full-Screen QR Viewer */}
      {mobileQrView && (
        <div className="fixed inset-0 z-[3000] bg-white flex flex-col items-center justify-center" onClick={() => setMobileQrView(null)}>
          <div className="absolute top-4 right-4">
            <button onClick={() => setMobileQrView(null)} className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 text-xl active:bg-gray-200">&times;</button>
          </div>
          <p className="mb-3 text-sm font-semibold text-gray-800">{mobileQrView.napCode}</p>
          <img
            src={`data:image/png;base64,${mobileQrView.qrData}`}
            alt="QR Code"
            className="w-[80vw] h-[80vw] max-w-[400px] max-h-[400px] object-contain"
          />
          <p className="mt-4 text-sm text-gray-500">Tap anywhere to close</p>
        </div>
      )}
    </div>
  );
}
